import { cn } from '@/lib/cn';

export function Card({
  className,
  children,
  ...rest
}: React.HTMLAttributes<HTMLDivElement>): React.JSX.Element {
  return (
    <div
      className={cn(
        'rounded-[var(--radius-panel)] border border-border bg-surface shadow-sm',
        className,
      )}
      {...rest}
    >
      {children}
    </div>
  );
}

export interface CardHeaderProps {
  readonly title: React.ReactNode;
  readonly description?: React.ReactNode | undefined;
  readonly action?: React.ReactNode | undefined;
  /**
   * Heading level. A card in a page whose h1 is the page title needs h2, and a card nested
   * inside a section needs h3. Hardcoding a level is how heading order gets broken, so the
   * caller says.
   */
  readonly headingLevel?: 2 | 3 | 4;
  readonly className?: string;
}

export function CardHeader({
  title,
  description,
  action,
  headingLevel = 2,
  className,
}: CardHeaderProps): React.JSX.Element {
  const Heading = `h${String(headingLevel)}` as 'h2' | 'h3' | 'h4';
  return (
    <div
      className={cn(
        'flex items-start justify-between gap-4 border-b border-border px-4 py-3',
        className,
      )}
    >
      <div className="min-w-0">
        <Heading className="truncate text-sm font-semibold text-fg">{title}</Heading>
        {description !== undefined ? (
          <p className="mt-0.5 text-xs text-fg-subtle">{description}</p>
        ) : null}
      </div>
      {action !== undefined ? <div className="shrink-0">{action}</div> : null}
    </div>
  );
}

export function CardBody({
  className,
  children,
}: {
  readonly className?: string;
  readonly children: React.ReactNode;
}): React.JSX.Element {
  return <div className={cn('p-4', className)}>{children}</div>;
}
